/**
 * actions.ts — a small hook for running commands from buttons (outside the
 * palette). On `approval_required` it asks for confirmation, then re-runs with
 * approval granted — preserving the boundary gate even for one-click actions.
 */
import { useState } from "react";
import { runCommand, type Ack } from "./api.ts";

export function useAction(): { status: string; busy: boolean; run: (name: string, input: unknown) => Promise<Ack | null> } {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (name: string, input: unknown): Promise<Ack | null> => {
    setBusy(true);
    setStatus("");
    try {
      let r = await runCommand(name, input);
      if (r.kind === "approval_required") {
        const ok = typeof window !== "undefined" && window.confirm(`${r.hint}\n\nThis crosses a governed boundary. Approve and execute "${name}"?`);
        if (!ok) {
          setStatus("Cancelled — boundary not crossed.");
          return null;
        }
        r = await runCommand(name, input, true);
      }
      if (r.kind === "ok") {
        setStatus(r.value.message ?? "Done.");
        return r.value;
      }
      setStatus(r.kind === "error" ? r.message : "Approval needed.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  return { status, busy, run };
}
